import { Link } from "@tanstack/react-router";
import {
  AuthConfig,
  Probe,
  Test,
  Threat,
  cn,
  greaterThanToday,
  parseDate,
  queries,
  useAuthConfig,
} from "@theprelude/core";
import {
  IconButton,
  InfoIcon,
  TooltipArrow,
  TooltipContent,
  Tooltip,
  TooltipTrigger,
  TooltipProvider,
} from "@theprelude/ds";
import classNames from "classnames";
import { createQuery } from "@/lib/create-query";

type MetricData = {
  threatCount: number;
  testCount: number;
  newThreatCount: number;
  endpointCount: number;
  activeEndpointCount: number;
  newEndpointCount: number;
};

function getMetricsData(
  threats: Threat[],
  tests: Test[],
  endpoints: Probe[],
): MetricData {
  const threatList = new Set(
    threats.filter((t) => t.tombstoned === null).map((a) => a.id),
  );
  const testList = new Set(
    tests.filter((t) => t.tombstoned === null).map((t) => t.id),
  );
  const newThreats = threats
    .filter((t) => t.tombstoned === null)
    .filter(
      (t) => t.published && greaterThanToday(parseDate(t.created), 7) === false,
    )
    .map((t) => t.id);

  const activeEndpoints = endpoints.filter(
    (e) => e.last_seen && greaterThanToday(parseDate(e.last_seen), 1) === false,
  );
  const newEndpoints = endpoints.filter(
    (e) => greaterThanToday(parseDate(e.created), 7) === false,
  );

  return {
    threatCount: threatList.size,
    testCount: testList.size,
    newThreatCount: newThreats.length,
    endpointCount: endpoints.length,
    activeEndpointCount: activeEndpoints.length,
    newEndpointCount: newEndpoints.length,
  };
}

const metricsQuery = (authConfig: AuthConfig) =>
  createQuery({
    queries: [
      queries.detect.threats(authConfig),
      queries.detect.tests(authConfig),
      queries.detect.endpoints(authConfig, 90),
    ],
    queryFn: getMetricsData,
  });

export function MetricsLoading() {
  return (
    <div className="grid grid-cols-3 gap-4 lg:grid-cols-6">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="h-[7.25rem] w-full animate-pulse rounded border border-stroke-line bg-secondary-background p-3"
        />
      ))}
    </div>
  );
}

const MetricCards = ({ grayOut = false }: { grayOut?: boolean }) => {
  const authConfig = useAuthConfig();
  const query = metricsQuery(authConfig).useSuspenseQuery();

  const {
    threatCount,
    testCount,
    newThreatCount,
    endpointCount,
    activeEndpointCount,
    newEndpointCount,
  } = query.data;

  return (
    <div className="grid grid-cols-3 gap-4 lg:grid-cols-6">
      <MetricCard
        grayOut={grayOut}
        link="/dashboard/threats"
        title={"Total Threats"}
        tooltip={"Total count of threats published by Prelude"}
        count={threatCount}
      />

      <MetricCard
        grayOut={grayOut}
        link="/dashboard/new-threats"
        title={"New Threats"}
        tooltip={"New threats published in the past 7 days"}
        count={newThreatCount}
        displayGreen={true}
      />

      <MetricCard
        grayOut={grayOut}
        link="/dashboard/tests"
        title={"Tests Available"}
        tooltip={
          "Total count of tests available including threat and standalone tests"
        }
        count={testCount}
      />

      <MetricCard
        grayOut={grayOut}
        link="/dashboard/endpoints"
        title={"Total Endpoints"}
        tooltip={"Count of endpoints registered and running the Prelude probe"}
        count={endpointCount}
      />

      <MetricCard
        grayOut={grayOut}
        link="/dashboard/active-endpoints"
        title={"Active Endpoints"}
        tooltip={
          "Count of endpoints that have checked in within the past 24 hours"
        }
        count={activeEndpointCount}
      />
      <MetricCard
        grayOut={grayOut}
        link="/dashboard/new-endpoints"
        title={"New Endpoints"}
        tooltip={
          "Count of endpoints that are new and have registered in the past 7 days."
        }
        count={newEndpointCount}
        displayGreen={true}
      />
    </div>
  );
};

const MetricCard: React.FC<{
  grayOut?: boolean;
  link: string;
  title: string;
  tooltip: string;
  count?: number;
  displayGreen?: boolean;
}> = ({ link, title, tooltip, count, displayGreen, grayOut = false }) => {
  return (
    <Link to={link}>
      {({ isActive }) => (
        <div
          className={cn(
            "h-[7.25rem] w-full rounded border border-stroke-line bg-surface-background p-3 transition-colors hover:bg-secondary-background dark:bg-surface-background dark:hover:bg-secondary-background",
            {
              ["bg-tertiary-background"]: isActive,
              ["hover:bg-tertiary-background"]: isActive,
              ["border-primary-foreground"]: isActive,
              ["opacity-40"]: grayOut && !isActive,
            },
          )}
        >
          <div className="flex items-center border-b border-stroke-line pb-2">
            <span
              className={cn(
                "grow truncate text-base font-medium",
                isActive && "text-white",
              )}
            >
              {title}
            </span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <IconButton icon={<InfoIcon />} />
                </TooltipTrigger>
                <TooltipContent className="w-60">
                  {tooltip}
                  <TooltipArrow />
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <h4
            className={classNames("pt-4 text-[32px] leading-[42px]", {
              ["text-status-green"]: displayGreen,
              ["text-white"]: isActive,
            })}
          >
            {count ?? "N/A"}
          </h4>
        </div>
      )}
    </Link>
  );
};

export default MetricCards;
